import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { useSupabaseQuery } from './useSupabase';
import { useAppStore } from '../stores/appStore';
import type { Campaign } from '../types';
import type { Database } from '../lib/database.types';
import type { PostgrestError } from '@supabase/supabase-js';

type CampaignInsert = Database['public']['Tables']['campaigns']['Insert'];
type CampaignUpdate = Database['public']['Tables']['campaigns']['Update'];

// Supabase returns joined fields as nested objects
interface CampaignRow extends Campaign {
  ai_agents?: { name: string; avatar: string } | null;
  waha_sessions?: { session_name: string } | null;
}

const MOCK_CAMPAIGNS: CampaignRow[] = [
  {
    id: '1', org_id: 'demo', name: 'Q1 SaaS Outreach', status: 'active',
    agent_id: '1', session_id: '1', contact_list_id: '1', template_id: null,
    schedule: { start_date: '2026-01-15', window_start: '09:00', window_end: '18:00', messages_per_10min: 5, timezone: 'Asia/Baku', daily_limit: 100, delay_min_seconds: 30, delay_max_seconds: 90 },
    stats: { total_contacts: 5000, sent: 4230, delivered: 4102, read: 3241, replied: 892, opted_out: 12, failed: 38 },
    variants: [],
    created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
    ai_agents: { name: 'Luna', avatar: '🌙' }, waha_sessions: { session_name: 'sales-main' },
  },
  {
    id: '2', org_id: 'demo', name: 'Enterprise Cold Intro', status: 'active',
    agent_id: '2', session_id: '1', contact_list_id: '2', template_id: null,
    schedule: { start_date: '2026-02-01', window_start: '09:00', window_end: '18:00', messages_per_10min: 5, timezone: 'Asia/Baku', daily_limit: 100, delay_min_seconds: 30, delay_max_seconds: 90 },
    stats: { total_contacts: 3000, sent: 1850, delivered: 1790, read: 1456, replied: 534, opted_out: 5, failed: 15 },
    variants: [],
    created_at: new Date().toISOString(), updated_at: new Date().toISOString(),
    ai_agents: { name: 'Atlas', avatar: '🗺️' }, waha_sessions: { session_name: 'sales-main' },
  },
];

export function useCampaign(): {
  campaigns: CampaignRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
  createCampaign: (data: Omit<CampaignInsert, 'org_id'>) => Promise<void>;
  updateCampaign: (id: string, data: CampaignUpdate) => Promise<void>;
  runCampaign: (campaignId: string) => Promise<void>;
  pauseCampaign: (campaignId: string) => Promise<void>;
} {
  const orgId = useAppStore(s => s.orgId);
  const [mutating, setMutating] = useState(false);

  const { data, loading, error, refetch } = useSupabaseQuery<CampaignRow[]>(
    async () => {
      if (!isSupabaseConfigured) return { data: MOCK_CAMPAIGNS, error: null };
      const { data: rows, error: err } = await supabase
        .from('campaigns')
        .select('*, ai_agents(name,avatar), waha_sessions(session_name)')
        .order('created_at', { ascending: false });
      return { data: rows as CampaignRow[] | null, error: err };
    },
    []
  );

  const createCampaign = useCallback(
    async (insertData: Omit<CampaignInsert, 'org_id'>) => {
      if (!isSupabaseConfigured) { toast.info('Connect Supabase to create campaigns'); return; }
      setMutating(true);
      try {
        const { error: err } = await supabase.from('campaigns').insert({ ...insertData, org_id: orgId! } as any);
        if (err) throw err;
        toast.success('Campaign created');
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to create campaign');
      } finally {
        setMutating(false);
      }
    },
    [orgId, refetch]
  );

  const updateCampaign = useCallback(
    async (id: string, updateData: CampaignUpdate) => {
      if (!isSupabaseConfigured) { toast.info('Connect Supabase to update campaigns'); return; }
      setMutating(true);
      try {
        const { error: err } = await supabase.from('campaigns').update(updateData).eq('id', id);
        if (err) throw err;
        toast.success('Campaign updated');
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update campaign');
      } finally {
        setMutating(false);
      }
    },
    [refetch]
  );

  const runCampaign = useCallback(
    async (campaignId: string) => {
      if (!isSupabaseConfigured) { toast.info('Connect Supabase to run campaigns'); return; }
      setMutating(true);
      try {
        const { error: err } = await supabase.functions.invoke('run-campaign', {
          body: { campaign_id: campaignId },
        });
        if (err) throw err;
        toast.success('Campaign started');
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to start campaign');
      } finally {
        setMutating(false);
      }
    },
    [refetch]
  );

  const pauseCampaign = useCallback(
    async (campaignId: string) => {
      if (!isSupabaseConfigured) { toast.info('Connect Supabase to pause campaigns'); return; }
      setMutating(true);
      try {
        const { error: err } = await supabase
          .from('campaigns')
          .update({ status: 'paused' })
          .eq('id', campaignId);
        if (err) throw err;
        toast.success('Campaign paused');
        refetch();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to pause campaign');
      } finally {
        setMutating(false);
      }
    },
    [refetch]
  );

  return {
    campaigns: data ?? (isSupabaseConfigured ? [] : MOCK_CAMPAIGNS),
    loading: loading || mutating,
    error,
    refetch,
    createCampaign,
    updateCampaign,
    runCampaign,
    pauseCampaign,
  };
}
